import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneCall, CheckCircle2, ClipboardList, Activity } from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { greetingKey } from "@/lib/format";
import DashboardTasks from "@/components/DashboardTasks";
import useStatusLabelMap from "@/lib/useStatusLabelMap";

function Kpi({ icon: Icon, label, value, hint, testid, onClick }) {
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`card flex flex-col gap-3 text-left w-full ${onClick ? "hover:border-[var(--teal-400)] hover:shadow-[0_8px_24px_-16px_rgba(14,90,94,0.25)] transition-all" : ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-lg bg-[var(--teal-50)] text-[var(--teal-700)] grid place-items-center">
          <Icon size={18} strokeWidth={1.75} />
        </div>
        <span className="text-[11px] text-[var(--ink-500)] uppercase tracking-wider">{label}</span>
      </div>
      <div className="font-display tabular text-[28px] leading-none text-[var(--ink-900)]">{value}</div>
      {hint && <div className="text-[12px] text-[var(--ink-500)]">{hint}</div>}
    </Wrapper>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [acts, setActs] = useState([]);
  const statusMap = useStatusLabelMap();

  useEffect(() => {
    Promise.all([
      http.get("/api/dashboard/stats"),
      http.get("/api/dashboard/recent-activity"),
    ]).then(([s, a]) => { setStats(s.data); setActs(a.data); });
  }, []);

  if (!stats) {
    return <div className="p-8 text-[var(--ink-500)]" data-testid="dashboard-loading">{t("common.loading")}</div>;
  }

  const firstName = user?.name?.split(" ")[0] || "";

  return (
    <div className="p-8 max-w-[1100px] mx-auto fade-in" data-testid="dashboard-screen">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
            {t("nav.dashboard")}
          </div>
          <h1 className="font-display text-[36px] leading-[1.05] text-[var(--ink-900)]">
            {t(greetingKey())}, {firstName}.
          </h1>
          <p className="text-[14px] text-[var(--ink-500)] mt-2" data-testid="dashboard-subheading">
            {t("dashboard.last_active")} · {t("dashboard.active_mandates", { count: stats.active_mandates })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/leads?status=new")}
          data-testid="btn-start-session"
          className="btn btn-primary"
        >
          <PhoneCall size={16} /> {t("dashboard.start_session")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Kpi icon={ClipboardList} label={t("dashboard.open_tasks")} value={stats.open_tasks}
             hint="Planning voor vandaag" testid="kpi-open-tasks"
             onClick={() => navigate("/tasks")} />
        <Kpi icon={CheckCircle2} label={t("dashboard.shortlist")} value={stats.shortlist}
             hint="Kansrijke panden" testid="kpi-shortlist"
             onClick={() => navigate("/shortlist")} />
        <Kpi icon={PhoneCall} label={t("dashboard.calls_week")} value={stats.calls_this_week}
             hint="Laatste 7 dagen" testid="kpi-calls-week" />
      </div>

      <DashboardTasks />

      <section className="mt-8">
        <h2 className="font-display text-[22px] text-[var(--ink-900)] mb-1">{t("dashboard.recent_activity")}</h2>
        <p className="text-[13px] text-[var(--ink-500)] mb-4">{t("dashboard.recent_sub")}</p>
        <div className="card p-0 overflow-hidden" data-testid="recent-activity-list">
          {acts.length === 0 ? (
            <div className="p-6 text-sm text-[var(--ink-500)]">{t("dashboard.empty_activity")}</div>
          ) : (
            <ul>
              {acts.map(a => (
                <li key={a.id} className="flex items-start gap-4 px-5 py-3 border-b border-[var(--border-soft)] last:border-b-0">
                  <div className="w-8 h-8 rounded-lg bg-[var(--teal-50)] text-[var(--teal-700)] grid place-items-center mt-0.5">
                    <Activity size={15} strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--ink-900)]">
                      <span className="font-semibold">{activityLabel(a, statusMap)}</span>
                      {a.lead_address && <span className="text-[var(--ink-500)]"> — {a.lead_address}, {a.lead_postal_code} {a.lead_city}</span>}
                    </div>
                    {a.payload?.note && (
                      <div className="text-[13px] text-[var(--ink-500)] mt-0.5">&ldquo;{a.payload.note}&rdquo;</div>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--ink-400)] whitespace-nowrap">
                    {new Date(a.created_at).toLocaleString("nl-BE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function activityLabel(a, statusMap = {}) {
  const newKey = a.payload?.new_status;
  const newStatusLabel = newKey ? (statusMap[newKey] || newKey) : "";
  const map = {
    call: "Gesprek gelogd",
    status_change: `Status → ${newStatusLabel}`,
    shortlist: a.payload?.shortlisted ? "Op shortlist geplaatst" : "Van shortlist gehaald",
    webhook_ingest: `Nieuwe lead via ${a.payload?.source || "Flowzy"}`,
    note: "Notitie toegevoegd",
  };
  return map[a.type] || a.type;
}
